import React from 'react';
import { Link } from 'react-router-dom';
import { Logo } from './Logo';
import { Button } from './ui/button';
import {
  NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuLink,
  NavigationMenuList, NavigationMenuTrigger,
} from './ui/navigation-menu';
import { ChevronRight } from 'lucide-react';
import { ThemeToggle } from '../lib/ThemeContext';
import { useLanguage } from '../lib/LanguageContext';
import LanguageSwitcher from './LanguageSwitcher';

export default function SiteHeader() {
  const { t } = useLanguage();

  const MENU = [
    {
      label: t('nav.accounts'), to: '/comptes',
      items: [
        { title: t('public.comptesTitle'), desc: t('features.accountsSavingsDesc'), to: '/comptes#courant' },
        { title: t('features.accountsSavings'), desc: t('features.accountsSavingsDesc'), to: '/comptes#livret' },
      ],
    },
    {
      label: t('nav.cards'), to: '/cartes',
      items: [
        { title: t('pricing.essentialName'), desc: t('pricing.freeCard'), to: '/cartes#basic' },
        { title: t('pricing.premiumName'), desc: t('pricing.cashback'), to: '/cartes#premium' },
        { title: t('pricing.metalName'), desc: t('pricing.metalCard'), to: '/cartes#elite' },
      ],
    },
    {
      label: t('nav.credits'), to: '/credits',
      items: [
        { title: t('nav.credits'), desc: t('public.creditsSubtitle'), to: '/credits#immobilier' },
      ],
    },
    {
      label: t('nav.insurance'), to: '/assurances',
      items: [
        { title: t('nav.insurance'), desc: t('public.assurancesSubtitle'), to: '/assurances#auto' },
      ],
    },
    {
      label: t('nav.stock'), to: '/bourse',
      items: [
        { title: t('nav.stock'), desc: t('public.bourseSubtitle'), to: '/bourse#pea' },
      ],
    },
    { label: t('nav.currencies'), to: '/devises' },
    { label: t('nav.help'), to: '/aide' },
  ];

  const PRO_LINK = { label: t('nav.professionals'), to: '/professionnels' };

  return (
    <header className="sticky top-0 z-50 bg-white/90 backdrop-blur border-b border-slate-200 dark:bg-slate-900/90 dark:border-slate-700">
      {/* Top bar : Particuliers / Pro */}
      <div className="bg-slate-50 border-b border-slate-100 dark:bg-slate-900 dark:border-slate-700">
        <div className="max-w-7xl mx-auto px-6 h-8 flex items-center justify-end gap-1 text-xs">
          <Link to="/" className="px-3 py-1 rounded-full font-semibold text-blue-900 bg-white border border-slate-200 dark:bg-slate-800 dark:text-blue-300 dark:border-slate-700">{t('nav.individuals')}</Link>
          <Link to={PRO_LINK.to} className="px-3 py-1 rounded-full text-slate-600 hover:text-blue-900 dark:text-slate-300 dark:hover:text-blue-300" data-testid="link-pro">{PRO_LINK.label}</Link>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/"><Logo /></Link>

        <NavigationMenu className="hidden lg:flex">
          <NavigationMenuList>
            {MENU.map((m) => (
              <NavigationMenuItem key={m.label}>
                {m.items ? (
                  <>
                    <NavigationMenuTrigger className="text-sm font-medium">{m.label}</NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <div className="p-4 w-[460px] grid grid-cols-2 gap-2">
                        {m.items.map((it) => (
                          <NavigationMenuLink asChild key={it.to}>
                            <Link to={it.to} className="block p-3 rounded-lg hover:bg-blue-50 transition">
                              <div className="font-medium text-sm text-slate-900">{it.title}</div>
                              <div className="text-xs text-slate-500 mt-0.5">{it.desc}</div>
                            </Link>
                          </NavigationMenuLink>
                        ))}
                        <NavigationMenuLink asChild>
                          <Link to={m.to} className="col-span-2 mt-1 inline-flex items-center gap-1 text-blue-700 text-xs font-semibold p-2">
                            {t('nav.viewAllLink')} <ChevronRight className="w-3 h-3"/>
                          </Link>
                        </NavigationMenuLink>
                      </div>
                    </NavigationMenuContent>
                  </>
                ) : (
                  <NavigationMenuLink asChild>
                    <Link to={m.to} className="px-3 py-2 text-sm font-medium hover:text-blue-700">{m.label}</Link>
                  </NavigationMenuLink>
                )}
              </NavigationMenuItem>
            ))}
          </NavigationMenuList>
        </NavigationMenu>

        <div className="flex items-center gap-2">
          <LanguageSwitcher variant="header" />
          <ThemeToggle />
          <Link to="/connexion"><Button variant="ghost" size="sm" data-testid="btn-header-login">{t('common.login')}</Button></Link>
          <Link to="/inscription"><Button size="sm" className="bg-blue-900 hover:bg-blue-800" data-testid="btn-header-signup">{t('common.signup')}</Button></Link>
        </div>
      </div>
      {/* Mobile sub-nav */}
      <div className="lg:hidden border-t border-slate-100 overflow-x-auto scrollbar-thin dark:border-slate-700">
        <div className="flex gap-1 px-3 py-2 whitespace-nowrap text-xs font-medium">
          {MENU.map((m) => (
            <Link key={m.label} to={m.to} className="px-3 py-1.5 rounded-full text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800">{m.label}</Link>
          ))}
          <Link to={PRO_LINK.to} className="px-3 py-1.5 rounded-full text-blue-700 bg-blue-50 hover:bg-blue-100 font-semibold dark:text-blue-300 dark:bg-blue-900/30 dark:hover:bg-blue-900/50">{PRO_LINK.label}</Link>
        </div>
      </div>
    </header>
  );
}
